import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSiteContent } from '../context/SiteContentContext';
import { portfolioData } from '../data/portfolioData';

const getYouTubeVideoId = (url) => {
  if (!url) return '';
  let videoId = '';
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  if (match && match[2].length === 11) {
    videoId = match[2];
  } else {
    const shortsRegExp = /\/shorts\/([a-zA-Z0-9_-]{11})/;
    const shortsMatch = url.match(shortsRegExp);
    if (shortsMatch) {
      videoId = shortsMatch[1];
    }
  }
  return videoId;
};

const getYouTubeEmbedUrl = (url) => {
  const videoId = getYouTubeVideoId(url);
  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }
  return url;
};

const PortfolioSection = () => {
    const { siteContent } = useSiteContent();
    const [activeTab, setActiveTab] = useState('Website Design');
    const tabs = ['Website Design', 'Logo', 'Corporate Branding', 'E-Commerce', 'Video', 'Animation'];

    const filteredData = portfolioData.filter(item => item.category === activeTab);


    return (
        <section className="w-full py-8 md:py-24 bg-transparent relative z-10 px-4">
            {/* Container - Transparent */}
            <div className="max-w-7xl mx-auto border border-zinc-800/50 bg-transparent rounded-3xl p-4 md:p-12">

                <div className="text-center mb-6 md:mb-10 space-y-4">
                    <h2 className="text-[#F3CA52] font-black uppercase tracking-[0.3em] text-xs">Our Portfolio</h2>
                    <h3 className="text-4xl md:text-5xl font-black text-white tracking-tight">Success Stories in Code & Design</h3>
                </div>

                {/* SEO Heading Above Tabs */}
                <div className="text-center mb-6">
                    <p className="text-sm md:text-base text-zinc-400 font-medium max-w-2xl mx-auto">
                        Browse our curated work — from SEO-optimized websites and logo designs to full corporate branding and e-commerce solutions.
                    </p>
                </div>

                {/* Tabs — Mobile Single Line Scroll (breaks out of inner padding) */}
                <div className="w-full mb-6 md:mb-16 -mx-6 md:mx-0">
                    <div className="flex flex-nowrap overflow-x-auto gap-2 px-6 pb-2 md:flex-wrap md:justify-center md:overflow-visible md:px-0 md:pb-0 scrollbar-hide">
                        {tabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`flex-none whitespace-nowrap px-4 py-2.5 md:px-6 md:py-3 rounded-lg border transition-all duration-300 font-black text-[10px] md:text-xs uppercase tracking-widest ${activeTab === tab
                                    ? 'btn-tab-active border-transparent'
                                    : 'bg-transparent border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-white'
                                    }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Responsive Grid: 2 Columns Mobile / 4 Columns Desktop */}
                <motion.div
                    layout
                    className="grid grid-cols-2 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 md:gap-6"
                >
                    <AnimatePresence mode='wait'>
                        {filteredData.map((item) => {
                            const isScrollable = item.category === 'Website Design' || item.category === 'E-Commerce' || item.category === 'Corporate Branding';
                            const hasVideo = !!item.videoUrl;
                            return (
                                <motion.div
                                    key={item.id}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    transition={{ duration: 0.3 }}
                                    className="group relative h-[180px] md:h-[320px] rounded-xl md:rounded-2xl overflow-hidden border border-zinc-800 hover:border-[#F3CA52] bg-zinc-950 cursor-pointer shadow-lg transition-colors duration-300"
                                >
                                    {hasVideo ? (
                                        <div className="absolute inset-0 w-full h-full pointer-events-none overflow-hidden bg-zinc-950">
                                            <iframe
                                                src={`${getYouTubeEmbedUrl(item.videoUrl)}?autoplay=1&mute=1&loop=1&playlist=${getYouTubeVideoId(item.videoUrl)}&controls=0&showinfo=0&rel=0&playsinline=1&enablejsapi=1`}
                                                title={item.title}
                                                className="w-full h-full scale-[1.3] pointer-events-none"
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            ></iframe>
                                        </div>
                                    ) : isScrollable ? (
                                        <div className="absolute top-0 left-0 w-full h-auto transition-transform duration-[5s] ease-in-out group-hover:-translate-y-[calc(100%-180px)] md:group-hover:-translate-y-[calc(100%-320px)]">
                                            <img 
                                                src={item.img} 
                                                alt={item.title} 
                                                className="w-full h-auto opacity-70 group-hover:opacity-100 transition-opacity duration-300" 
                                            />
                                        </div>
                                    ) : (
                                        <div className="absolute inset-0 w-full h-full flex items-center justify-center p-4 md:p-6 bg-zinc-900/30">
                                            <img 
                                                src={item.img} 
                                                alt={item.title} 
                                                className="max-w-full max-h-full object-contain opacity-75 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300" 
                                            />
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent pointer-events-none" />
                                    <div className="absolute bottom-0 left-0 right-0 p-3 md:p-6 flex flex-col justify-end pointer-events-none">
                                        <h4 className="text-xs md:text-lg font-black text-white truncate">{item.title}</h4>
                                        <p className="text-[#F3CA52] text-[8px] md:text-[10px] font-bold uppercase tracking-widest mt-0.5 md:mt-1">{item.category}</p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                </motion.div>

                {/* Button */}
                <div className="mt-12 md:mt-16 text-center">
                    <button
                        onClick={() => window.location.href = '/portfolio'}
                        className="btn-animated-gold"
                    >
                        Check out our Client Case Studies in Brief
                    </button>
                </div>
            </div>
        </section>
    );
};

export default PortfolioSection;